/**
 * Header - top bar with transport, undo/redo, save/load, and scope selector
 *
 * Uses ScopeContext for navigation and stats.
 */

import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TransportControls } from './TransportControls';
import { InstanceTree } from './InstanceTree';
import { projectService } from '../../serverClient/projectService';
import { webRuntimeApi } from '@/runtime/web';
import { useHistory } from '../hooks/useHistory';
import { useScope } from '../contexts/ScopeContext';
import { useAppState } from '../contexts/AppContext';
import { logger, LogLevel } from '@/logging';
import { useRuntime } from '../contexts/RuntimeProvider';
import { confirm } from './ConfirmDialog';
import { PerformanceModeToggle } from './PerfomanceModeToggle';
import { JukeboxControls } from './JukeboxControls';
import { HelpPage } from './HelpPage';

const LOG_LEVELS: LogLevel[] = [LogLevel.Error, LogLevel.Info, LogLevel.Trace];
const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  [LogLevel.Error]: '#ef4444',
  [LogLevel.Info]: '#3b82f6',
  [LogLevel.Trace]: '#6b7280'
};


export function Header() {
  const { canUndo, canRedo, undo, redo } = useHistory();
  const { path, pathLabels, setPath, instanceId, navigateUp, canNavigateUp, selectedNodeIds, clearSelection } = useScope();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { openProjectBrowser, projectName, projectPath, setProject, logError } = useAppState();
  const [globalLogLevel, setGlobalLogLevel] = useState<LogLevel>(LogLevel.Trace);
  const [showHelp, setShowHelp] = useState(false);
  const {
    encapsulate, getAffectedExposedEndpoints, syncEnabled, setSyncEnabled,
  } = useRuntime();

  const handleLogLevelChange = useCallback((level: LogLevel) => {
    logger.setLogLevel(level);
    setGlobalLogLevel(level);
  }, [logger]);



  // Handle creating definition from selected nodes
  const handleEncapsulate = useCallback(async () => {
    if (selectedNodeIds.length < 1) return;

    const name = prompt('Definition name:');
    if (!name) return;

    // Validate name (must be valid identifier)
    if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(name)) {
      alert('Name must start with a letter and contain only letters and numbers');
      return;
    }

    // Check for affected exposed endpoints
    const affected = getAffectedExposedEndpoints(instanceId, selectedNodeIds);
    let preserveContainerApi = true;

    if (affected.length > 0) {
      preserveContainerApi = await confirm({
        title: 'Exposed Endpoints',
        message: `The selected nodes have ${affected.length} exposed endpoint(s): ${affected.join(', ')}. Keep them exposed on the container?`,
        confirmLabel: 'Keep exposed',
        cancelLabel: 'Remove from container'
      });
    }

    try {
      encapsulate(instanceId, selectedNodeIds, name, preserveContainerApi);
    } catch (err) {
      alert(`Failed to create definition: ${err}`);
    }
  }, [selectedNodeIds, instanceId, getAffectedExposedEndpoints, encapsulate]);

  /** Save tweaks sidecar if snapshots exist */
  const saveTweaksIfNeeded = useCallback(async (name: string) => {
    const tweaks = webRuntimeApi.getTweaksFile();
    if (tweaks.snapshots.length > 0 || tweaks.jukeboxEnabled) {
      await projectService.saveTweaks(name, tweaks);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (projectPath) {
      // Overwrite current project using full path
      await projectService.save(projectPath);
      await saveTweaksIfNeeded(projectPath);
    } else {
      // Unsaved draft - prompt for name
      const name = prompt('Project name:');
      if (!name) return;
      await projectService.save(name);
      await saveTweaksIfNeeded(name);
      setProject(name, name);
      navigate(`/${sessionId}/project/${encodeURIComponent(name)}`);
    }
  }, [projectPath, setProject, navigate, sessionId, saveTweaksIfNeeded]);

  const handleSaveAs = useCallback(async () => {
    const name = prompt('Save as:', projectName ?? '');
    if (!name) return;
    await projectService.save(name);
    await saveTweaksIfNeeded(name);
    setProject(name, name);
    navigate(`/${sessionId}/project/${encodeURIComponent(name)}`);
  }, [projectName, setProject, navigate, sessionId, saveTweaksIfNeeded]);

  const handleNew = useCallback(async () => {
    const confirmed = await confirm({ title: 'New', message: 'Start a new project? Unsaved changes will be lost.' });
    if (!confirmed) return;
    // Navigate to new draft - ProjectLoader handles reset
    const draftId = crypto.randomUUID().slice(0, 8);
    navigate(`/${sessionId}/new/${draftId}`);
    // Sync to server in background (optimistic)
    if (syncEnabled) {
      projectService.clear().catch(err => logError('Header', `Failed to sync clear: ${err}`));
    }
  }, [navigate, sessionId, syncEnabled, logError]);



  return (
    <div style={styles.header}>
  {/*    <div style={styles.title}>Tweakable</div>*/}

      <TransportControls />

      {/* Undo/Redo */}
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={undo}
          disabled={!canUndo}
          style={{
            ...styles.button,
            opacity: canUndo ? 1 : 0.4,
            cursor: canUndo ? 'pointer' : 'default'
          }}
          title="Undo (Ctrl+Z)"
        >
          ↩
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          style={{
            ...styles.button,
            opacity: canRedo ? 1 : 0.4,
            cursor: canRedo ? 'pointer' : 'default'
          }}
          title="Redo (Ctrl+Shift+Z)"
        >
          ↪
        </button>
      </div>

      {/* New/Save/Load */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleNew} style={styles.button}>New</button>
        <button onClick={handleSave} style={styles.button}>Save</button>
        {projectName && (
          <button onClick={handleSaveAs} style={styles.button}>Save As</button>
        )}
        <button onClick={openProjectBrowser} style={styles.button}>Load</button>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Back button */}
        {canNavigateUp && (
          <button
            onClick={navigateUp}
            style={styles.button}
            title="Go up one level"
          >
            ↑
          </button>
        )}

        {/* Instance tree dropdown */
          <InstanceTree />
        }

        {/* Breadcrumb display */}
        {path.length > 1 && (
          <div style={styles.breadcrumbs}>
            {pathLabels.map((label, i) => {
              const isLast = i === pathLabels.length - 1;
              const pathToHere = path.slice(0, i + 1);
              return (
                <span key={i}>
                  {i > 0 && <span style={{ color: '#6b7280', margin: '0 4px' }}>/</span>}
                  <span
                    onClick={isLast ? undefined : () => setPath(pathToHere)}
                    style={{
                      color: isLast ? 'white' : '#9ca3af',
                      cursor: isLast ? 'default' : 'pointer',
                      textDecorationLine: isLast ? 'none' : 'underline',
                      textDecorationColor: '#6b7280'
                    }}
                  >
                    {label}
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Encapsulate button - always visible, disabled when no selection */}

      <button
        onClick={handleEncapsulate}
        disabled={selectedNodeIds.length < 1}
        style={{
          ...styles.iconButton,
          opacity: selectedNodeIds.length < 1 ? 0.3 : 1,
          cursor: selectedNodeIds.length < 1 ? 'default' : 'pointer'
        }}
        title={selectedNodeIds.length < 1
          ? 'Select nodes to encapsulate'
          : 'Encapsulate'
        }
      >
        ⧈
      </button>

   <PerformanceModeToggle/>

      {/* Selection count and clear */}
      {selectedNodeIds.length > 0 && (
        <div style={styles.selectionGroup}>
          <span style={styles.selectionCount}>
            {selectedNodeIds.length} selected
          </span>
          <button onClick={clearSelection} style={styles.clearButton}>
            ✕
          </button>
        </div>
      )}

      {/* Project name - right aligned
      <div style={styles.rightGroup}>
        {projectName && (
          <span
            style={styles.projectName}
            onClick={() => window.dispatchEvent(new Event('show-info-toast'))}
            title="Show project info"
          >
            {projectName}
          </span>
        )}
      </div>
       */}

      {/* Server Sync Toggle 
      <button
        onClick={() => setSyncEnabled(!syncEnabled)}
        style={{
          ...styles.syncButton,
          background: syncEnabled ? '#22c55e' : '#374151',
          borderColor: syncEnabled ? '#16a34a' : '#4b5563'
        }}
        title={syncEnabled ? 'Server sync enabled - click to disable' : 'Server sync disabled - click to enable'}
      >
        {syncEnabled ? '⟳ Sync' : '⟳ Sync'}
      </button>
      */}

      {/* Global Log Level 
      <
      <div style={styles.logLevelGroup}>
        {LOG_LEVELS.map(level => (
          <button
            key={level}
            onClick={() => handleLogLevelChange(level)}
            style={{
              ...styles.logLevelButton,
              background: globalLogLevel === level ? LOG_LEVEL_COLORS[level] : '#333',
              color: globalLogLevel === level ? '#fff' : '#666'
            }}
            title={`Set global log level to ${level}`}
          >
            {level.charAt(0).toUpperCase()}
          </button>
        ))}
      </div>
      */}

      <JukeboxControls />

      {/* Help */}
      <button
        onClick={() => setShowHelp(true)}
        style={styles.iconButton}
        title="Node reference"
      >
        ?
      </button>
      <HelpPage open={showHelp} onClose={() => setShowHelp(false)} />

    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '12px 20px',
    background: '#1f2937',
    borderBottom: '1px solid #374151'
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold'
  },
  projectName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#60a5fa',
    cursor: 'pointer'
  },
  breadcrumbs: {
    fontSize: 13,
    fontFamily: 'monospace'
  },
  button: {
    background: '#374151',
    color: 'white',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '4px 12px',
    fontSize: 12,
    cursor: 'pointer'
  },
  iconButton: {
    background: '#374151',
    color: 'white',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 16,
    lineHeight: 1,
    cursor: 'pointer'
  },
  rightGroup: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  selectionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 8px',
    background: '#374151',
    borderRadius: 4
  },
  selectionCount: {
    color: '#9ca3af',
    fontSize: 12
  },
  clearButton: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: 14,
    cursor: 'pointer',
    padding: '2px 4px'
  },
  logLevelGroup: {

    display: 'flex',
    gap: 2
  },
  logLevelButton: {
    border: 'none',
    borderRadius: 3,
    padding: '4px 6px',
    fontSize: 10,
    fontWeight: 'bold',
    cursor: 'pointer',
    minWidth: 20
  },
  syncButton: {
    color: 'white',
    border: '1px solid',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 11,
    cursor: 'pointer',
    fontWeight: 500
  },
};
